import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { createPortal } from "react-dom";
import { ContextualPopover } from "./ContextualPopover";

type RowAction = "insert" | "duplicate" | "up" | "down" | "delete";

export function ContextualOverlayLayer({ documentRef, enabled, rowCount, onRowAction, onReorder }: {
  documentRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  rowCount: number;
  onRowAction(action: RowAction, index: number): void;
  onReorder?(from: number, to: number): void;
}) {
  const [hover, setHover] = useState<DOMRect | null>(null);
  const [focus, setFocus] = useState<DOMRect | null>(null);
  const [row, setRow] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const toolRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const focusMenuOnOpen = useRef(false);
  const pointerRow = useRef<number | null>(null);
  const focusedRow = useRef<number | null>(null);
  const draggingRow = useRef<number | null>(null);
  const hoveredTarget = useRef<Element | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const root = documentRef.current?.parentElement;
    if (!root) return;
    const paint = (target: Element | null, focused: boolean, pointer?: PointerEvent) => {
      if (target?.closest(".document-row-action, .document-row-menu, .document-drag-handle")) return;
      const editable = target?.closest("[data-edit-target]");
      const inside = editable && root.contains(editable);
      const lineContent = inside && editable.closest(".document-line-content");
      (focused ? setFocus : setHover)(inside && !lineContent ? editable.getBoundingClientRect() : null);
      const line = target?.closest<HTMLElement>("[data-document-row][data-row-index]");
      let active = line && root.contains(line) ? Number(line.dataset.rowIndex) : null;
      if (!focused && pointerRow.current !== null) {
        const prior = root.querySelector<HTMLElement>(`[data-document-row][data-row-index="${pointerRow.current}"]`)?.getBoundingClientRect();
        if (prior && pointer) {
          const { clientX: x, clientY: y } = pointer;
          const beside = y >= prior.top - 8 && y <= prior.bottom + 8 && ((x >= prior.left - 40 && x <= prior.left) || (x >= prior.right && x <= prior.right + 48));
          if (beside) active = pointerRow.current;
        }
      }
      if (focused) focusedRow.current = active;
      else pointerRow.current = active;
      if (menuOpen) return;
      const next = pointerRow.current ?? focusedRow.current ?? (rowCount > 0 ? 0 : null);
      setRow(next);
    };
    const move = (event: PointerEvent) => { hoveredTarget.current = document.elementFromPoint(event.clientX, event.clientY); paint(hoveredTarget.current, false, event); };
    const focusIn = (event: FocusEvent) => paint(event.target as Element, true);
    const focusOut = () => requestAnimationFrame(() => paint(document.activeElement, true));
    const refresh = () => { paint(document.activeElement, true); paint(hoveredTarget.current?.isConnected ? hoveredTarget.current : null, false); };
    let frame = 0;
    const scheduleRefresh = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(refresh); };
    const dragOver = (event: DragEvent) => { if (draggingRow.current !== null && event.target instanceof Element && root.contains(event.target)) event.preventDefault(); };
    const drop = (event: DragEvent) => {
      if (draggingRow.current === null) return;
      const target = (event.target as Element | null)?.closest<HTMLElement>("[data-document-row][data-row-index]");
      if (target && root.contains(target)) { event.preventDefault(); onReorder?.(draggingRow.current, Number(target.dataset.rowIndex)); }
      draggingRow.current = null;
    };
    const dragStart = (event: DragEvent) => {
      const handle = (event.target as Element | null)?.closest<HTMLButtonElement>(".document-drag-handle");
      const line = handle?.closest<HTMLElement>("[data-document-row][data-row-index]");
      if (!line || !root.contains(line)) return;
      draggingRow.current = Number(line.dataset.rowIndex);
      if (event.dataTransfer) { event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", String(draggingRow.current)); }
    };
    const dragEnd = () => { draggingRow.current = null; };
    const handleClick = (event: MouseEvent) => {
      const handle = (event.target as Element | null)?.closest<HTMLButtonElement>(".document-drag-handle");
      const line = handle?.closest<HTMLElement>("[data-document-row][data-row-index]");
      if (!handle || !line || !root.contains(line)) return;
      const index = Number(line.dataset.rowIndex);
      const nextOpen = row === index ? !menuOpen : true;
      focusMenuOnOpen.current = nextOpen && event.detail === 0;
      toolRef.current?.setAttribute("aria-expanded", "false");
      toolRef.current = handle;
      handle.setAttribute("aria-expanded", String(nextOpen));
      setRow(index);
      setMenuOpen(nextOpen);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("focusin", focusIn);
    document.addEventListener("focusout", focusOut);
    root.addEventListener("input", scheduleRefresh);
    window.addEventListener("scroll", refresh, true);
    window.addEventListener("resize", refresh);
    const observer = new ResizeObserver(scheduleRefresh);
    observer.observe(root);
    // A numeric edit can reflow the paper before any pointer/scroll event.
    // Re-measure live anchors rather than retaining a ring at its old rectangle.
    refresh();
    root.addEventListener("dragover", dragOver);
    root.addEventListener("drop", drop);
    root.addEventListener("dragstart", dragStart);
    root.addEventListener("dragend", dragEnd);
    root.addEventListener("click", handleClick);
    return () => { cancelAnimationFrame(frame); observer.disconnect(); root.removeEventListener("input", scheduleRefresh); document.removeEventListener("pointermove", move); document.removeEventListener("focusin", focusIn); document.removeEventListener("focusout", focusOut); window.removeEventListener("scroll", refresh, true); window.removeEventListener("resize", refresh); root.removeEventListener("dragover", dragOver); root.removeEventListener("drop", drop); root.removeEventListener("dragstart", dragStart); root.removeEventListener("dragend", dragEnd); root.removeEventListener("click", handleClick); };
  }, [documentRef, enabled, menuOpen, row, rowCount, onReorder]);

  useEffect(() => { if (toolRef.current) toolRef.current.setAttribute("aria-expanded", String(menuOpen)); }, [menuOpen]);

  useEffect(() => {
    if (menuOpen && focusMenuOnOpen.current) {
      menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus({ preventScroll: true });
      focusMenuOnOpen.current = false;
    }
  }, [menuOpen, row]);

  const closeMenu = () => {
    const trigger = toolRef.current;
    const root = documentRef.current?.parentElement;
    const index = Number(trigger?.closest<HTMLElement>("[data-document-row]")?.dataset.rowIndex ?? row ?? 0);
    setMenuOpen(false);
    requestAnimationFrame(() => {
      if (!root?.isConnected || menuRef.current?.isConnected) return;
      if (trigger?.isConnected && !trigger.disabled) {
        trigger.focus({ preventScroll: true });
        return;
      }
      const handles = Array.from(root.querySelectorAll<HTMLButtonElement>(".document-drag-handle:not(:disabled)"));
      handles.sort((a, b) => Math.abs(Number(a.closest<HTMLElement>("[data-document-row]")?.dataset.rowIndex) - index)
        - Math.abs(Number(b.closest<HTMLElement>("[data-document-row]")?.dataset.rowIndex) - index));
      (handles[0] ?? root.querySelector<HTMLButtonElement>(".document-add-line"))?.focus({ preventScroll: true });
    });
  };

  const navigateMenu = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)'));
    if (items.length === 0) return;
    event.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 :
      event.key === "ArrowDown" ? (current + 1) % items.length : (current - 1 + items.length) % items.length;
    items[next]?.focus({ preventScroll: true });
  };

  if (!enabled) return null;
  const ring = (box: DOMRect | null, className: string) => box ? <span className={`document-overlay-ring ${className}`} style={{ left: box.left - 4, top: box.top - 4, width: box.width + 8, height: box.height + 8 }} /> : null;
  return createPortal(<div className="document-contextual-overlay" aria-hidden={undefined}>
    {ring(hover, "document-overlay-ring--hover")}{ring(focus, "document-overlay-ring--focus")}
    {menuOpen && row !== null ? <ContextualPopover key={row} anchorRef={toolRef} label={`Line ${row + 1} actions`} onClose={closeMenu} width={220}><div ref={menuRef} className="document-row-menu" role="menu" onKeyDown={navigateMenu}>
      {([ ["insert", "Insert line below"], ["duplicate", "Duplicate"], ["up", "Move up"], ["down", "Move down"] ] as const).map(([action, title]) => <button key={action} type="button" role="menuitem" disabled={(action === "up" && row === 0) || (action === "down" && row === rowCount - 1)} onClick={() => { onRowAction(action, row); closeMenu(); }}>{title}</button>)}
    </div></ContextualPopover> : null}
  </div>, document.body);
}
